import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Settings, Trash2, ChevronRight, ChevronDown } from 'lucide-react';

const DEFAULT_LAYOUT = 'Main View';

// Group order = panel order. The Customer list omits Pipeline (filtered
// out by the bucket-and-drop logic inside ColumnToggle when no columns
// reference it); the Leads list still surfaces it.
//
// "Customer Details" was renamed to "Site Details" after the contact /
// site restructure — the columns underneath were always site-level
// data (site number, POS type, fuel brand, install date, renewal
// date) and the "customer" name caused confusion now that those
// values come from the primary site row instead of the legacy
// customer_details columns.
export const COLUMN_GROUPS = [
  { key: 'contact',        label: 'Contact Information' },
  { key: 'address',        label: 'Address' },
  { key: 'phoneEmail',     label: 'Phone & Email' },
  { key: 'site',           label: 'Site Details' },
  { key: 'accountSummary', label: 'Account Summary' },
  { key: 'pipeline',       label: 'Pipeline' },
  { key: 'credentials',    label: 'Credentials' },
  { key: 'xsiteio',        label: 'xsiteio' }
];

// useColumnPreferences
// -------------------------------------------------------------------
// Manages named layouts with OPTIMISTIC writes so checkbox clicks
// register instantly in the UI — the server PUT happens in the
// background. Previously the controlled `checked` prop was pinned to
// whatever the server had returned, so a slow/failed PUT made the
// checkboxes appear inert.
//
// API:
//   useColumnPreferences('customers', defaultKeys, allKeys?)
//
// `defaultKeys` is the initial set rendered when no saved layout
// exists. `allKeys` is the universe of legal column keys — used to
// drop stale references in saved layouts (e.g. a column that was
// renamed or deleted). When `allKeys` isn't supplied it falls back
// to `defaultKeys`, which is correct ONLY if every registered column
// is in the default set. As soon as any column is marked
// defaultHidden the caller must pass allKeys explicitly, otherwise
// toggling a hidden column on visually appears to do nothing — the
// memo would silently strip the just-added key on the next render.
export function useColumnPreferences(databaseType, defaultKeys, allKeys) {
  const STORAGE_KEY = `columns_active_${databaseType}`;
  const validKeys = allKeys || defaultKeys;
  const [layouts, setLayouts] = useState([]);
  const [activeName, setActiveName] = useState(() =>
    (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || DEFAULT_LAYOUT
  );
  const [ready, setReady] = useState(false);

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/preferences/columns/${databaseType}`)
      .then((res) => { if (!cancelled) setLayouts(res.data?.layouts || []); })
      .catch((err) => console.warn('Column prefs load failed:', err.response?.data?.error || err.message))
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseType]);

  // Persist active layout per browser.
  useEffect(() => {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, activeName);
  }, [activeName, STORAGE_KEY]);

  // Visible keys = the active layout's stored columns, falling back to
  // defaults when the layout doesn't exist yet. Filter against
  // validKeys (the full registry) so that turning on a defaultHidden
  // column actually sticks.
  const visibleKeys = useMemo(() => {
    const layout = layouts.find((l) => l.layout_name === activeName);
    if (!layout) return defaultKeys;
    const filtered = (layout.visible_columns || []).filter((k) => validKeys.includes(k));
    return filtered.length > 0 ? filtered : defaultKeys;
  }, [layouts, activeName, defaultKeys, validKeys]);

  // Optimistic upsert: update local state SYNCHRONOUSLY, then fire the
  // server PUT in the background. The visible-keys memo re-computes
  // immediately so the UI feels responsive even on a slow network.
  // If the PUT fails we log a warning — local state already reflects
  // the user's intent so the rest of the session still works.
  const persistLayout = useCallback((name, keys) => {
    const cleanName = (name || '').trim();
    if (!cleanName) return;
    const now = new Date().toISOString();
    setLayouts((prev) => {
      const idx = prev.findIndex((l) => l.layout_name === cleanName);
      const row = { layout_name: cleanName, visible_columns: keys, updated_at: now };
      if (idx >= 0) {
        const next = [...prev]; next[idx] = row; return next;
      }
      const next = [...prev, row];
      next.sort((a, b) => {
        if (a.layout_name === DEFAULT_LAYOUT) return -1;
        if (b.layout_name === DEFAULT_LAYOUT) return 1;
        return a.layout_name.localeCompare(b.layout_name);
      });
      return next;
    });
    axios.put(`/api/preferences/columns/${databaseType}`, {
      layout_name: cleanName,
      visible_columns: keys
    }).catch((err) => {
      console.warn('Column prefs save failed:', err.response?.data?.error || err.message);
    });
  }, [databaseType]);

  // Toggle: applies to the currently active layout. Optimistic — the
  // checkbox visual updates immediately.
  const setVisibleKeys = useCallback((nextKeys) => {
    persistLayout(activeName, nextKeys);
  }, [activeName, persistLayout]);

  // Save under a (possibly new) name; switches active to that layout.
  const saveLayout = useCallback((name, keys) => {
    persistLayout(name, keys);
    setActiveName(name.trim());
  }, [persistLayout]);

  const deleteLayout = useCallback((name) => {
    if (name === DEFAULT_LAYOUT) return;
    setLayouts((prev) => prev.filter((l) => l.layout_name !== name));
    if (activeName === name) setActiveName(DEFAULT_LAYOUT);
    axios.delete(`/api/preferences/columns/${databaseType}/${encodeURIComponent(name)}`)
      .catch((err) => console.warn('Column prefs delete failed:', err.response?.data?.error || err.message));
  }, [databaseType, activeName]);

  return {
    visibleKeys, setVisibleKeys,
    layouts, activeName, setActiveName,
    saveLayout, deleteLayout, ready
  };
}

// LayoutDropdown — unchanged
export function LayoutDropdown({ layouts, activeName, onChange }) {
  const names = layouts.map((l) => l.layout_name);
  if (!names.includes(DEFAULT_LAYOUT)) names.unshift(DEFAULT_LAYOUT);
  return (
    <select
      className="layout-dropdown"
      value={activeName}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Saved layouts"
      title="Switch layout"
    >
      {names.map((name) => <option key={name} value={name}>{name}</option>)}
    </select>
  );
}

// TriStateCheckbox
// -------------------------------------------------------------------
// Thin wrapper that pushes the `indeterminate` DOM property (React
// doesn't pass it through JSX attributes). No event-handler tricks —
// the surrounding <label> takes care of click delegation.
function TriStateCheckbox({ checked, indeterminate, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
    />
  );
}

// ColumnToggle
// -------------------------------------------------------------------
// Gear icon → grouped checkbox panel. Groups have a master tri-state
// checkbox and an optional expand-to-see-fields drawer. Toggling either
// the master or a field auto-saves to the active layout.
export default function ColumnToggle({ columns, value, onChange, activeName, onSaveAs, onDelete }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [expanded, setExpanded] = useState({});
  const rootRef  = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, minWidth: 240 });

  // Portalled panel — same approach as ColumnFilterMenu. Position with
  // position: fixed + viewport coords so the panel escapes any
  // overflow:auto / stacking-context clipping above it.
  function positionPanel() {
    const btn = rootRef.current?.querySelector('.column-toggle-button');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const panelWidth = panelRef.current?.offsetWidth || 280;
    const gap = 6;
    let left = rect.right - panelWidth;
    if (left < 8) left = 8;
    const top = rect.bottom + gap;
    setPanelPos({ top, left, minWidth: Math.max(280, panelWidth) });
  }

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e) {
      const inTrigger = rootRef.current && rootRef.current.contains(e.target);
      const inPanel   = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    function onReflow() { positionPanel(); }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open]);

  useEffect(() => { if (open) positionPanel(); }, [open]);
  useEffect(() => { if (!open) setNewName(''); }, [open]);

  const groups = useMemo(() => {
    const buckets = {};
    for (const c of columns) {
      // Default group for unclassified columns is "site" — that's where
      // operational columns land in the renamed taxonomy. Anything
      // referencing the old "customer" key is silently rebucketed.
      const g = (c.group === 'customer' ? 'site' : c.group) || 'site';
      if (!buckets[g]) buckets[g] = [];
      buckets[g].push(c);
    }
    return COLUMN_GROUPS
      .map((g) => ({ ...g, cols: buckets[g.key] || [] }))
      .filter((g) => g.cols.length > 0);
  }, [columns]);

  const setKeys = useCallback((nextKeys) => {
    if (nextKeys.length === 0) return;
    onChange(nextKeys);
  }, [onChange]);

  const toggleColumn = useCallback((key) => {
    setKeys(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
  }, [value, setKeys]);

  const toggleGroup = useCallback((groupKey) => {
    const groupCols = columns.filter((c) => c.group === groupKey);
    const groupKeys = groupCols.map((c) => c.key);
    const allVisible = groupKeys.every((k) => value.includes(k));
    let next;
    if (allVisible) {
      next = value.filter((k) => !groupKeys.includes(k));
    } else {
      const toAdd = groupKeys.filter((k) => !value.includes(k));
      next = [...value, ...toAdd];
    }
    setKeys(next);
  }, [columns, value, setKeys]);

  function showAll() { setKeys(columns.map((c) => c.key)); }

  function handleSaveAs(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name || !onSaveAs) return;
    onSaveAs(name);
    setNewName('');
  }

  const isDefaultActive = activeName === DEFAULT_LAYOUT;

  return (
    <div className="column-toggle" ref={rootRef}>
      <button
        type="button"
        className="column-toggle-button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Toggle column visibility"
        title={`Show/hide columns (${value.length} of ${columns.length} visible)`}
      >
        <Settings size={16} strokeWidth={1.75} />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="column-toggle-panel"
          role="dialog"
          aria-label="Visible columns"
          style={{
            position: 'fixed',
            top:  panelPos.top,
            left: panelPos.left,
            minWidth: panelPos.minWidth
          }}
        >
          <div className="column-toggle-head">
            <div>
              <span className="column-toggle-title">Visible columns</span>
              <span className="column-toggle-sub">{activeName}</span>
            </div>
            <button type="button" className="column-toggle-show-all" onClick={showAll}>
              Show all
            </button>
          </div>
          <ul className="column-toggle-groups">
            {groups.map((g) => {
              const groupKeys = g.cols.map((c) => c.key);
              const visibleCount = groupKeys.filter((k) => value.includes(k)).length;
              const allOn = visibleCount === groupKeys.length;
              const indeterminate = visibleCount > 0 && visibleCount < groupKeys.length;
              const isExpanded = !!expanded[g.key];
              return (
                <li key={g.key} className="column-toggle-group">
                  <div className="column-toggle-group-head">
                    <label className="column-toggle-group-label">
                      <TriStateCheckbox
                        checked={allOn}
                        indeterminate={indeterminate}
                        onChange={() => toggleGroup(g.key)}
                      />
                      <span className="column-toggle-group-name">{g.label}</span>
                      <span className="column-toggle-group-count">
                        {visibleCount}/{groupKeys.length}
                      </span>
                    </label>
                    <button
                      type="button"
                      className="column-toggle-group-expand"
                      onClick={() => setExpanded((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? `Collapse ${g.label}` : `Expand ${g.label}`}
                    >
                      {isExpanded
                        ? <ChevronDown size={14} strokeWidth={2} />
                        : <ChevronRight size={14} strokeWidth={2} />}
                    </button>
                  </div>
                  {isExpanded && (
                    <ul className="column-toggle-fields">
                      {g.cols.map((c) => {
                        const checked = value.includes(c.key);
                        return (
                          <li key={c.key}>
                            <label className="column-toggle-field">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleColumn(c.key)}
                              />
                              <span>{c.label}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
          <form className="column-toggle-saveas" onSubmit={handleSaveAs}>
            <label className="column-toggle-saveas-label">Save current as new layout</label>
            <div className="column-toggle-saveas-row">
              <input
                type="text"
                placeholder="e.g. Short View"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={100}
              />
              <button type="submit" className="btn-primary" disabled={!newName.trim()}>
                Save as
              </button>
            </div>
          </form>
          <div className="column-toggle-foot">
            <span>{value.length} of {columns.length} visible</span>
            {!isDefaultActive && onDelete && (
              <button
                type="button"
                className="column-toggle-delete"
                onClick={() => {
                  if (window.confirm(`Delete layout "${activeName}"?`)) onDelete(activeName);
                }}
                title={`Delete layout "${activeName}"`}
                aria-label={`Delete layout ${activeName}`}
              >
                <Trash2 size={14} strokeWidth={1.75} />
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
